import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, Flame, MapPin, MessageCircle, Smartphone, Star } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";
import { useLocation } from "@/context/LocationContext";
import api from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { MapView } from "@/components/MapView";
import { Footer } from "@/components/Footer";
import { LogoMarquee } from "@/components/LogoMarquee";
import { HeroBanner } from "@/components/HeroBanner";
import { HomeAd } from "@/components/HomeAd";
import { TextTicker } from "@/components/TextTicker";
import { SponsoredSlider } from "@/components/SponsoredSlider";
import { SwipeHintRow } from "@/components/SwipeHintRow";
import { initSeen, unreadCls } from "@/lib/seenPosts";

function Section({ title, icon: Icon, to, children }) {
  const nav = useNavigate();
  return (
    <section className="mt-6 px-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          {Icon && <Icon className="w-5 h-5 text-emerald-500" />} {title}
        </h2>
        {to && (
          <button data-testid={`see-all-${to}`} onClick={() => nav(to)} className="flex items-center gap-0.5 text-sm font-semibold text-emerald-600">
            Shiko të gjitha <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { loc } = useLocation();
  const [near, setNear] = useState([]);
  const [newest, setNewest] = useState([]);
  const [top, setTop] = useState([]);
  const [posts, setPosts] = useState([]);
  const [points, setPoints] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const coords = loc.coords;
    const nearReq = coords ? api.get(`/near?lat=${coords.lat}&lng=${coords.lng}&limit=6`) : api.get("/newest?limit=6");
    nearReq.then((r) => setNear(r.data)).catch(() => {});
    api.get("/newest?limit=6").then((r) => setNewest(r.data)).catch(() => {});
    const tp = new URLSearchParams({ limit: "10" });
    if (coords) { tp.set("lat", coords.lat); tp.set("lng", coords.lng); }
    if (loc.city && loc.city !== "Afër meje") tp.set("city", loc.city);
    api.get(`/top-rated?${tp}`).then((r) => setTop(r.data)).catch(() => {});
    api.get("/community/posts").then((r) => { initSeen(r.data); setPosts(r.data.slice(0, 3)); }).catch(() => {});
    api.get("/map").then((r) => setPoints(r.data)).catch(() => {});
  }, [loc.coords, loc.city]);

  const doSearch = (e) => { e.preventDefault(); nav(`/kerko?q=${encodeURIComponent(q)}`); };

  return (
    <div className="gs-fade">
      {/* Hero */}
      <HeroBanner q={q} setQ={setQ} onSubmit={doSearch} />
      <TextTicker />

      {/* Category grid */}
      <section className="px-4 mt-4">
        <div className="grid grid-cols-2 gap-3">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                data-testid={`category-card-${c.key}`}
                onClick={() => nav(c.route || `/kategoria/${c.key}`)}
                className={`bg-gradient-to-br ${c.grad} text-white rounded-3xl p-4 text-left shadow-md hover:-translate-y-1 hover:shadow-lg transition-all`}
              >
                <Icon className={c.iconName?.startsWith("Img_") ? "w-14 h-14 -mt-1 mb-1" : "w-11 h-11 -mt-1 mb-4"} strokeWidth={2} />
                <div className="font-bold text-base leading-tight">{c.label}</div>
                <div className="text-xs text-white/85 mt-0.5">{c.tagline}</div>
              </button>
            );
          })}
        </div>
      </section>

      <HomeAd />

      <LogoMarquee coords={loc.coords} />

      {/* Near you */}
      <Section title="Afër teje" icon={MapPin} to="/kerko">
        <SwipeHintRow count={near.length} data-testid="near-row" className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
          {near.map((l) => (
            <div key={l.id} className="w-60 shrink-0"><ListingCard listing={l} /></div>
          ))}
          {!near.length && <p className="text-sm text-slate-400">Nuk ka ende shpallje.</p>}
        </SwipeHintRow>
      </Section>

      {/* Newest */}
      <Section title="Të rejat" icon={Flame} to="/kerko">
        <SwipeHintRow count={newest.length} data-testid="newest-row" className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1 snap-x">
          {newest.map((l) => (
            <div key={l.id} className="w-60 shrink-0 snap-start"><ListingCard listing={l} /></div>
          ))}
        </SwipeHintRow>
      </Section>

      <SponsoredSlider coords={loc.coords} />

      {/* Top rated */}
      <Section title="Më të vlerësuarat" icon={Star}>
        <SwipeHintRow count={top.length} data-testid="top-rated-row" className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1 snap-x">
          {top.map((l) => (
            <div key={l.id} className="w-60 shrink-0 snap-start"><ListingCard listing={l} hash="vleresimet" /></div>
          ))}
          {!top.length && <p className="text-sm text-slate-400" data-testid="top-rated-empty">Ende nuk ka biznese të vlerësuara afër teje.</p>}
        </SwipeHintRow>
      </Section>

      {/* Community */}
      <Section title="Komunitete" icon={MessageCircle} to="/komunitete">
        <div className="space-y-2">
          {posts.map((p) => (
            <button key={p.id} data-testid={`home-post-${p.id}`} onClick={() => nav(`/komunitete/${p.id}`)} className={`w-full text-left ${unreadCls(p)} rounded-2xl p-3.5 border shadow-sm hover:shadow-md transition-all`}>
              <div className="flex items-center gap-2 text-xs text-cyan-600 font-semibold mb-1">
                <span className="bg-cyan-50 px-2 py-0.5 rounded-full">{p.topic}</span>
                <span className="text-slate-400">{p.city}, {p.country}</span>
              </div>
              <div className="font-semibold text-slate-800 text-sm">{p.title}</div>
              <div className="text-xs text-slate-400 mt-1">{p.comment_count} përgjigje · {p.likes?.length || 0} pëlqime</div>
            </button>
          ))}
        </div>
      </Section>

      {/* Map */}
      <Section title="Harta" icon={MapPin} to="/harta">
        <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
          <MapView points={points} height={240} interactive={false} />
        </div>
      </Section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
